import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormGroup, FormLabel, Stack, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import PermissionSelector, { PermissionOption } from './PermissionSelector';

interface RoleFormValues {
    name: string;
    slug: string;
    description: string;
    permissionIds: string[];
}

interface RoleDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    role?: { _id: string; name: string; slug: string; description?: string; permissions: PermissionOption[] } | null;
    permissions: PermissionOption[];
    onClose: () => void;
    onSubmit: (values: RoleFormValues) => Promise<void>;
}

const RoleDialog: React.FC<RoleDialogProps> = ({ open, mode, role, permissions, onClose, onSubmit }) => {
    const [formValues, setFormValues] = useState<RoleFormValues>({
        name: '',
        slug: '',
        description: '',
        permissionIds: [],
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (mode === 'edit' && role) {
            setFormValues({
                name: role.name,
                slug: role.slug,
                description: role.description || '',
                permissionIds: role.permissions.map(permission => permission._id),
            });
        } else {
            setFormValues({ name: '', slug: '', description: '', permissionIds: [] });
        }
    }, [open, mode, role]);

    const generateSlug = (name: string): string => {
        return name
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-');
    };

    const handleChange = (field: keyof RoleFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (field === 'name') {
            setFormValues(prev => ({ ...prev, name: value, slug: generateSlug(value) }));
        } else {
            setFormValues(prev => ({ ...prev, [field]: value }));
        }
    };

    const handlePermissionChange = (permissionIds: string[]) => {
        setFormValues(prev => ({ ...prev, permissionIds }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            await onSubmit(formValues);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>
                {mode === 'create' ? 'Créer un rôle' : 'Modifier le rôle'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3}>
                    <Stack direction="row" spacing={2}>
                        <FormGroup sx={{ flex: 1 }}>
                            <FormLabel>Nom</FormLabel>
                            <TextField value={formValues.name} onChange={handleChange('name')} fullWidth />
                        </FormGroup>
                        <FormGroup sx={{ flex: 1 }}>
                            <FormLabel>Identifiant</FormLabel>
                            <TextField value={formValues.slug} disabled fullWidth />
                        </FormGroup>
                    </Stack>
                    <FormGroup>
                        <FormLabel>Déscription</FormLabel>
                        <TextField
                            value={formValues.description}
                            onChange={handleChange('description')}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                    </FormGroup>
                    <FormGroup>
                        <FormLabel sx={{ fontWeight: 700, mb: 1 }}>
                            Autorisations
                        </FormLabel>
                        <Box>
                            <PermissionSelector
                                permissions={permissions}
                                value={formValues.permissionIds}
                                onChange={handlePermissionChange}
                                label=""
                            />
                        </Box>
                    </FormGroup>
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant="text" color="secondary">
                    Annuler
                </Button>
                <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}>
                    {mode === 'create' ? 'Créer' : 'Enregistrer'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RoleDialog;
